import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import './ServiceDetailsPage.css';
import { servicesAPI, cartAPI, ordersAPI, isAuthenticated } from '../../services/api';
import { useToast } from '../../Components/Toast/Toast';

const ServiceDetailsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const fetchService = useCallback(async () => {
    try {
      setLoading(true);
      const response = await servicesAPI.getServiceBySlug(slug);
      if (response.success) {
        setService(response.data.service);
      } else {
        setError(response.message || 'Service not found');
      }
    } catch (error) {
      console.error('Error fetching service:', error);
      setError('Failed to fetch service details');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (location.state?.service) {
      setService(location.state.service);
      setLoading(false);
    } else {
      fetchService();
    }
  }, [slug, location.state, fetchService]);

  const handleAddToCart = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const response = await cartAPI.addToCart(service._id, quantity);
      if (response.success) {
        toast.success('Service added to cart successfully!');
        navigate('/myProfile?tab=cart');
      } else {
        toast.error(response.message || 'Failed to add service to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add service to cart');
    }
  };

  const handleDownloadTemplate = async () => {
    if (!isAuthenticated()) {
      toast.info('Please login to download the template');
      navigate('/login');
      return;
    }
    try {
      setDownloadingTemplate(true);
      const blob = await servicesAPI.downloadRawTemplate(service.slug);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${service.name.replace(/\s+/g, '_')}_Template.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded successfully!');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error(error.message || 'Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleOrderNow = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      // Add to cart first
      const cartResponse = await cartAPI.addToCart(service._id, quantity);
      if (cartResponse.success) {
        // Create order directly and redirect to orders page
        const orderResponse = await ordersAPI.createOrder('cash_on_delivery');
        if (orderResponse.success) {
          toast.success('Order placed successfully! You can now upload documents for your order.');
          navigate('/orders');
        } else {
          toast.error('Failed to place order. Please try again.');
        }
      } else {
        toast.error(cartResponse.message || 'Failed to add service to cart');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getProcessingTypeColor = (type) => {
    switch (type) {
      case 'automatic': return '#10B981';
      case 'manual': return '#F59E0B';
      case 'hybrid': return '#3B82F6';
      default: return '#6B7280';
    }
  };

  const getProcessingTypeText = (type) => {
    switch (type) {
      case 'automatic': return 'AI Processing';
      case 'manual': return 'Expert Review';
      case 'hybrid': return 'AI + Expert';
      default: return 'Processing';
    }
  };

  // Auto-scroll to top when page first loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  if (loading) {
    return (
      <div className="service-details-page">
        <div className="service-loading">
          <div className="loading-spinner"></div>
          <p>Loading service details...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="service-details-page">
        <div className="service-error">
          <h2>Service Not Found</h2>
          <p>{error || 'The service you are looking for does not exist.'}</p>
          <button onClick={() => navigate('/services')} className="btn-primary">
            Back to Services
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="service-details-page">
      <div className="service-details-container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <button onClick={() => navigate('/services')} className="breadcrumb-link">
            Services
          </button>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">{service.name}</span>
        </div>

        <div className="service-details-grid">
          {/* Main Content */}
          <div className="service-main">
            <div className="service-header">
              <h1 className="service-title">{service.name}</h1>
              <div
                className="processing-type"
                style={{ backgroundColor: getProcessingTypeColor(service.processingType) }}
              >
                {getProcessingTypeText(service.processingType)}
              </div>
            </div>

            <div className="service-description">
              <h2>Description</h2>
              <p>{service.description}</p>
            </div>

            <div className="service-instructions">
              <h2>Processing Instructions</h2>
              <p>{service.instructions || 'Our team will process your documents according to the highest standards and deliver the results within the estimated time frame.'}</p>
            </div>

            <div className="service-requirements">
              <h2>Required Documents</h2>
              <div className="requirements-list">
                {service.requiredDocuments.map((doc, index) => (
                  <div key={index} className="requirement-item">
                    <div className="requirement-header">
                      <div className="requirement-name-row">
                        <h3>{doc.name}</h3>
                        <button
                          className="download-template-btn"
                          onClick={handleDownloadTemplate}
                          disabled={downloadingTemplate}
                          data-tooltip="Download raw template"
                        >
                          {downloadingTemplate ? '⏳' : '📥'} {downloadingTemplate ? 'Downloading...' : 'Download Template'}
                        </button>
                      </div>
                      <span className={`requirement-status ${doc.isRequired ? 'required' : 'optional'}`}>
                        {doc.isRequired ? 'Required' : 'Optional'}
                      </span>
                    </div>
                    <div className="requirement-details">
                      <p><strong>Accepted Formats:</strong> {doc.acceptedFormats.join(', ').toUpperCase()}</p>
                      <p><strong>Maximum Size:</strong> {doc.maxSize}MB</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="service-sidebar">
            <div className="pricing-card">
              <div className="price">
                {formatPrice(service.price)}
              </div>
              <div className="price-details">
                <p>One-time payment</p>
                <p>No hidden fees</p>
              </div>
            </div>

            <div className="service-info">
              <div className="info-item">
                <span className="info-label">⏱️ Processing Time:</span>
                <span className="info-value">{service.estimatedTime}</span>
              </div>
              <div className="info-item">
                <span className="info-label">📋 Category:</span>
                <span className="info-value">{service.category.replace('_', ' ').toUpperCase()}</span>
              </div>
              <div className="info-item">
                <span className="info-label">🔧 Processing Type:</span>
                <span className="info-value">{getProcessingTypeText(service.processingType)}</span>
              </div>
            </div>

            <div className="quantity-selector">
              <label htmlFor="quantity">Quantity:</label>
              <div className="quantity-controls">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="quantity-btn"
                >
                  -
                </button>
                <input
                  type="number"
                  id="quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  className="quantity-input"
                />
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="quantity-btn"
                >
                  +
                </button>
              </div>
            </div>

            <div className="total-price">
              <span>Total: {formatPrice(service.price * quantity)}</span>
            </div>

            <div className="action-buttons">
              <button
                onClick={handleAddToCart}
                className="btn-secondary"
              >
                Add to Cart
              </button>
              <button
                onClick={handleOrderNow}
                className="btn-primary"
              >
                Order Now
              </button>
            </div>

            <div className="service-guarantees">
              <h3>Our Guarantees</h3>
              <ul>
                <li>✅ Professional quality results</li>
                <li>✅ On-time delivery</li>
                <li>✅ 24/7 customer support</li>
                <li>✅ Money-back guarantee</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailsPage;
